import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for background data refresh with local change preservation.
 * Automatically refreshes data at specified intervals while preserving
 * any unsaved local changes.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.fetchData - Async function to fetch fresh data
 * @param {number} [options.interval=30000] - Refresh interval in milliseconds (default: 30s)
 * @param {Function} [options.mergeData] - Function to merge fresh data with local state: (freshData, localChanges, currentData) => mergedData
 * @param {Function} [options.getLocalChanges] - Returns the latest unsaved edits; read on every refresh
 *   (timed or manual) that doesn't pass `localChanges` itself. Required for `mergeData` to see edits on
 *   timed refreshes.
 * @param {Function} [options.getCurrentData] - Returns the latest grid rows; read on every refresh that
 *   doesn't pass `currentData` itself.
 * @param {boolean} [options.enabled=true] - Whether background refresh is enabled
 * @param {Function} [options.onRefreshStart] - Callback when refresh starts
 * @param {Function} [options.onRefreshComplete] - Callback when refresh completes: (data) => void
 * @param {Function} [options.onRefreshError] - Callback when refresh fails: (error) => void
 * @param {boolean} [options.refreshOnMount=false] - Whether to fetch immediately on mount
 * @returns {Object} Background refresh state and controls
 *
 * @example
 * const { isRefreshing, lastRefreshed, refresh, startRefresh, stopRefresh } = useBackgroundRefresh({
 *   fetchData: () => getBrokerEntries(),
 *   interval: 30000,
 *   getLocalChanges: () => updatedRowsRef.current,
 *   getCurrentData: () => rowDataRef.current,
 *   mergeData: (freshData, localChanges, currentData) => {
 *     // Merge fresh server data with local unsaved changes
 *     return freshData.map(row => {
 *       const localChange = localChanges[row.id];
 *       return localChange ? { ...row, ...localChange } : row;
 *     });
 *   },
 *   onRefreshComplete: (data) => setRowData(data),
 * });
 */
export const useBackgroundRefresh = ({
  fetchData,
  interval = 30000,
  mergeData,
  getLocalChanges,
  getCurrentData,
  enabled = true,
  onRefreshStart,
  onRefreshComplete,
  onRefreshError,
  refreshOnMount = false,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [error, setError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);

  // Refs to track latest values without causing effect re-runs
  const intervalRef = useRef(null);
  const enabledRef = useRef(enabled);
  const fetchDataRef = useRef(fetchData);
  const mergeDataRef = useRef(mergeData);
  const getLocalChangesRef = useRef(getLocalChanges);
  const getCurrentDataRef = useRef(getCurrentData);
  const onRefreshCompleteRef = useRef(onRefreshComplete);
  const onRefreshErrorRef = useRef(onRefreshError);
  const onRefreshStartRef = useRef(onRefreshStart);

  // Keep refs updated
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    mergeDataRef.current = mergeData;
  }, [mergeData]);

  useEffect(() => {
    getLocalChangesRef.current = getLocalChanges;
  }, [getLocalChanges]);

  useEffect(() => {
    getCurrentDataRef.current = getCurrentData;
  }, [getCurrentData]);

  useEffect(() => {
    onRefreshCompleteRef.current = onRefreshComplete;
  }, [onRefreshComplete]);

  useEffect(() => {
    onRefreshErrorRef.current = onRefreshError;
  }, [onRefreshError]);

  useEffect(() => {
    onRefreshStartRef.current = onRefreshStart;
  }, [onRefreshStart]);

  /**
   * Performs a single refresh operation
   * @param {Object} options - Refresh options
   * @param {Object} [options.localChanges] - Local changes to preserve during merge
   *   (default: `getLocalChanges()`, else `{}`)
   * @param {Array} [options.currentData] - Current data for merge operation
   *   (default: `getCurrentData()`, else `[]`)
   * @param {boolean} [options.silent=false] - If true, doesn't update isRefreshing state
   */
  const refresh = useCallback(
    async ({ localChanges, currentData, silent = false } = {}) => {
      if (!enabledRef.current) return null;

      try {
        if (!silent) {
          setIsRefreshing(true);
        }
        setError(null);
        onRefreshStartRef.current?.();

        const freshData = await fetchDataRef.current();

        // If merge function provided, merge fresh data with local changes.
        // Edits are read after the fetch resolves, so one made while the
        // request was in flight is still kept.
        let finalData = freshData;
        if (mergeDataRef.current && typeof mergeDataRef.current === 'function') {
          const changes = localChanges ?? getLocalChangesRef.current?.() ?? {};
          const current = currentData ?? getCurrentDataRef.current?.() ?? [];
          finalData = mergeDataRef.current(freshData, changes, current);
        }

        setLastRefreshed(new Date());
        setRefreshCount((prev) => prev + 1);
        onRefreshCompleteRef.current?.(finalData);

        return finalData;
      } catch (err) {
        setError(err);
        onRefreshErrorRef.current?.(err);
        return null;
      } finally {
        if (!silent) {
          setIsRefreshing(false);
        }
      }
    },
    [] // No dependencies - uses refs
  );

  /**
   * Starts the background refresh interval
   */
  const startRefresh = useCallback(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only start if enabled and interval > 0
    if (enabledRef.current && interval > 0) {
      intervalRef.current = setInterval(() => {
        // Silent background refresh; edits and rows come from
        // getLocalChanges / getCurrentData inside refresh().
        refresh({ silent: true });
      }, interval);
    }
  }, [interval, refresh]);

  /**
   * Stops the background refresh interval
   */
  const stopRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Manually trigger a single refresh (non-silent)
   */
  const manualRefresh = useCallback(
    (options = {}) => {
      return refresh({ ...options, silent: false });
    },
    [refresh]
  );

  // Set up interval on mount and when enabled changes
  useEffect(() => {
    if (enabled && interval > 0) {
      startRefresh();
    } else {
      stopRefresh();
    }

    return () => {
      stopRefresh();
    };
  }, [enabled, interval, startRefresh, stopRefresh]);

  // Optional refresh on mount
  useEffect(() => {
    if (refreshOnMount && enabled) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  return {
    /** Whether a refresh is currently in progress */
    isRefreshing,
    /** Timestamp of last successful refresh */
    lastRefreshed,
    /** Last error from refresh attempt */
    error,
    /** Number of successful refreshes */
    refreshCount,
    /** Manually trigger a refresh (shows loading state) */
    refresh: manualRefresh,
    /** Trigger a silent background refresh */
    silentRefresh: refresh,
    /** Start the automatic refresh interval */
    startRefresh,
    /** Stop the automatic refresh interval */
    stopRefresh,
  };
};

/**
 * Creates a merge function for preserving local row changes during refresh.
 * This is a common pattern for grids with unsaved edits.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.getRowId - Function to get unique ID from a row
 * @param {Function} [options.isNewRow] - Function to check if a row is newly created locally
 * @returns {Function} Merge function for useBackgroundRefresh
 *
 * @example
 * const mergeFunction = createMergeWithLocalChanges({
 *   getRowId: (row) => row.BROKER_ID,
 *   isNewRow: (row) => row.TEMP_ROW_ID != null,
 * });
 *
 * useBackgroundRefresh({
 *   fetchData: fetchBrokers,
 *   mergeData: mergeFunction,
 * });
 */
export const createMergeWithLocalChanges = ({ getRowId, isNewRow }) => {
  return (freshData, localChanges, currentData) => {
    // Apply local changes to fresh server data
    const mergedServerData = freshData.map((row) => {
      const rowId = getRowId(row);
      const changes = localChanges[rowId];

      if (changes) {
        // Apply each changed field
        const modifiedRow = { ...row };
        Object.entries(changes).forEach(([field, change]) => {
          if (change && change.newValue !== undefined) {
            modifiedRow[field] = change.newValue;
          }
        });
        return modifiedRow;
      }

      return row;
    });

    // Collect new rows that exist only locally
    const newRows = [];
    if (currentData && isNewRow) {
      currentData.forEach((row) => {
        if (isNewRow(row)) {
          newRows.push(row);
        }
      });
    }

    // New rows at the top, then merged server data
    return [...newRows, ...mergedServerData];
  };
};

export default useBackgroundRefresh;
